import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import ThemeSwitch from '../components/ThemeSwitch';
import { handleApiErrorWithMessages } from '../../utils/apiErrorHandler';

const EmailChangeConfirmCurrentPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setGeneralError('Invalid confirmation link');
      setIsLoading(false);
      return;
    }

    const confirmCurrentEmail = async () => {
      try {
        setIsLoading(true);
        setGeneralError(null);
        
        // Make GET request to confirm current email
        const response = await api.get(`/api/auth/change-email/confirm-current?token=${encodeURIComponent(token)}`);
        
        // Extract new_email from response
        if (response.data && response.data.new_email) {
          setNewEmail(response.data.new_email);
        }
        
        setIsSuccess(true);
      } catch (error) {
        const { generalError: apiGeneralError } = handleApiErrorWithMessages(error);
        setGeneralError(apiGeneralError || 'Failed to confirm email change request');
      } finally {
        setIsLoading(false);
      }
    };

    confirmCurrentEmail();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#121212] py-12 px-4 sm:px-6 relative">
      {/* Theme Switch - Top Right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
        <ThemeSwitch />
      </div>
      
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#121212] dark:text-[#D0D0D0]">
            {isLoading ? 'Confirming...' : isSuccess ? 'Email change request confirmed' : 'Error'}
          </h1>
          <p className="mt-2 text-sm text-[#7B7B7B] dark:text-[#848484]">
            {isLoading ? (
              'Please wait...'
            ) : isSuccess ? (
              <>
                Email change request confirmed. A confirmation email has been sent to your new email address
                {newEmail && <strong className="text-[#121212] dark:text-[#D0D0D0]"> ({newEmail})</strong>}.
                Please check your inbox and click the confirmation link.
                <br />
                <Link
                  to="/v2"
                  className="font-medium text-[#121212] dark:text-[#D0D0D0] hover:underline mt-4 inline-block"
                >
                  Go to dashboard
                </Link>
              </>
            ) : (
              <>
                {generalError || 'An error occurred'}
                <br />
                <Link
                  to="/v2"
                  className="font-medium text-[#121212] dark:text-[#D0D0D0] hover:underline mt-4 inline-block"
                >
                  Go to dashboard
                </Link>
              </>
            )}
          </p>
        </div>

        {/* Loading Spinner */}
        {isLoading && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#121212] dark:border-[#D0D0D0]"></div>
          </div>
        )}

        {/* Error Display */}
        {generalError && !isLoading && (
          <div className="rounded-xl border border-red-500 bg-red-50 dark:bg-red-900/20 p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{generalError}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailChangeConfirmCurrentPage;
